/* eslint-disable camelcase */
import React, { useCallback, useMemo } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useApp } from '../hooks/use-app';
import { RestApiForm } from '../tasks/types';
import { SystemAppNameRefMap, SystemTypeMap } from './types';
import RecordDeleteDialog, { TASK_AND_RECURRENCE_VALUE, DeleteOption } from './record-delete-dialog';

type DeleteMutationParams = {
  recordID: string;
  shouldStopRecurrence: boolean;
}

export interface Props {
  recordToDelete: { id: string; name: string, rrule: string | null };
  restForm: RestApiForm | undefined;
  callback: (id?: string) => void;
}

/**
 * A method that returns the key associated with the name for the system_app
 */
export function getRef(restForm: RestApiForm | undefined): string | undefined {
  let nameRef;
  if (restForm) {
    nameRef = SystemAppNameRefMap[restForm.system_type];
  }

  return nameRef;
}

export default function RecordDelete({ recordToDelete, restForm, callback }: Props) {
  const queryClient = useQueryClient();
  const app = useApp();

  const { id, rrule } = recordToDelete;

  const { reset, mutate, isError } = useMutation<unknown, unknown, DeleteMutationParams>(
    ({ recordID, shouldStopRecurrence }) => app.api.records.delete(recordID, null, null, {
      body: {
        stop_recurrence: shouldStopRecurrence,
      },
    }), {
      onSuccess: () => {
        queryClient.invalidateQueries();
        handleReset(id);
      },
    },
  );

  const handleReset = (recordId?: string) => {
    callback(recordId);
    setTimeout(() => isError && reset(), 400); // resets after the animation
  };

  const systemType = useMemo(() => SystemTypeMap[restForm?.system_type], [restForm]);

  const handlePositive = useCallback((deleteOption: DeleteOption) => {
    const shouldStopRecurrence = systemType === 'task' && deleteOption === TASK_AND_RECURRENCE_VALUE;
    mutate({ recordID: id, shouldStopRecurrence });
  }, [id]);

  return (
    <RecordDeleteDialog
      open={!!id}
      hasError={isError}
      hasRecurrence={!!rrule}
      recordType={systemType}
      onPositive={handlePositive}
      onNegative={() => handleReset()}
    />
  );
}
